/**
 * Carte « démo obligatoire » (formulaire de créneau). Utilisée :
 *  - en overlay flouté par-dessus le tableau de bord (AppShell) quand demoRequired,
 *  - en page plein écran de secours (/demo-requise).
 */
import { useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { api } from "../../lib/api";
import { launchDemo } from "../../lib/demo/demoMode";

const SLOTS = [
  { value: "Matin (9h–12h)", label: "Matin (9h–12h)" },
  { value: "Après-midi (14h–18h)", label: "Après-midi (14h–18h)" },
];

export function DemoRequiseCard() {
  const { refreshMe, logout, user } = useAuth();
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("Matin (9h–12h)");
  const [tel, setTel] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Lève le gate automatiquement dès qu'un super-admin valide la démo.
  useEffect(() => {
    const t = setInterval(() => {
      void refreshMe();
    }, 20000);
    return () => clearInterval(t);
  }, [refreshMe]);

  async function submit() {
    if (!date) {
      setErr("Choisissez une date.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.tenants.setDemoCreneau({
        creneau: `${date} · ${slot}`,
        telephone: tel.trim() || undefined,
        note: note.trim() || undefined,
      });
      setSent(true);
    } catch {
      setErr("Échec de l'envoi. Réessayez.");
    } finally {
      setBusy(false);
    }
  }

  const inputCls =
    "w-full rounded-xl border border-navy-200 bg-navy-50/40 px-4 py-3 text-sm text-navy-800 placeholder:text-navy-400 transition-colors focus:border-accent-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-accent-500/10";
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl shadow-navy-900/20 ring-1 ring-navy-100">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-accent-50 text-accent-600">
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polygon points="23 7 16 12 23 17 23 7" />
          <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
        </svg>
      </div>
      <h1 className="mt-6 text-center text-2xl font-bold tracking-tight text-navy-800">
        Une démo avant de démarrer
      </h1>

      {sent ? (
        <>
          <div className="mt-4 rounded-2xl bg-emerald-50 p-4 text-center text-sm text-emerald-800 ring-1 ring-emerald-200">
            <p className="font-semibold">Demande envoyée ✓</p>
            <p className="mt-1">
              Notre équipe vous recontacte pour confirmer le créneau. Votre espace
              s'ouvre dès la démo réalisée.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setSent(false)}
            className="mt-4 w-full text-center text-xs font-semibold text-navy-500 hover:text-navy-800"
          >
            Modifier ma demande
          </button>
        </>
      ) : (
        <>
          <p className="mt-3 text-center text-[15px] leading-relaxed text-navy-600">
            {user?.prenom ? `${user.prenom}, votre` : "Votre"} accès est prêt&nbsp;!
            Avant l'ouverture complète, on réalise ensemble une courte démo en
            visio (15–20 min). Choisissez un créneau&nbsp;:
          </p>

          <div className="mt-5 space-y-3 text-left">
            <div>
              <label className="mb-1.5 block text-[13px] font-semibold text-navy-700">Date souhaitée</label>
              <input type="date" min={today} value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="mb-1.5 block text-[13px] font-semibold text-navy-700">Créneau</label>
              <select value={slot} onChange={(e) => setSlot(e.target.value)} className={inputCls}>
                {SLOTS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-[13px] font-semibold text-navy-700">Téléphone</label>
              <input type="tel" value={tel} onChange={(e) => setTel(e.target.value)} placeholder="06 12 34 56 78" className={inputCls} />
            </div>
            <div>
              <label className="mb-1.5 block text-[13px] font-semibold text-navy-700">
                Message <span className="font-normal text-navy-400">(facultatif)</span>
              </label>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Une précision, une dispo particulière…" className={inputCls} />
            </div>
          </div>

          {err ? (
            <p className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>
          ) : null}

          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy}
            className="mt-5 w-full rounded-full bg-accent-500 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-accent-500/30 transition-colors hover:bg-accent-400 disabled:opacity-50"
          >
            {busy ? "Envoi…" : "Envoyer ma demande de créneau"}
          </button>
        </>
      )}

      <div className="mt-5 rounded-2xl bg-navy-50/70 p-4 text-center ring-1 ring-navy-100">
        <p className="text-[13px] font-semibold text-navy-700">
          Envie de jeter un œil tout de suite&nbsp;?
        </p>
        <p className="mt-0.5 text-xs text-navy-500">
          Explorez une agence fictive, en lecture seule, sans attendre la démo.
        </p>
        <button
          type="button"
          onClick={() => launchDemo()}
          className="mt-3 inline-flex items-center gap-2 rounded-full border border-accent-200 bg-white px-5 py-2.5 text-sm font-bold text-accent-700 transition-colors hover:bg-accent-50"
        >
          <span aria-hidden="true">✨</span>
          Explorer la démo en attendant
        </button>
      </div>

      <div className="mt-4 flex items-center justify-center gap-4">
        <button type="button" onClick={() => void refreshMe()} className="text-xs font-semibold text-navy-500 transition-colors hover:text-navy-800">
          J'ai fait ma démo · actualiser
        </button>
        <span className="text-navy-200" aria-hidden="true">·</span>
        <button type="button" onClick={() => void logout()} className="text-xs text-navy-400 transition-colors hover:text-navy-600">
          Se déconnecter
        </button>
      </div>
    </div>
  );
}
